import CreateOfferComponent from "@components/CreateOfferComponent/CreateOfferComponent";
import CustomSelect from "@components/CustomSelect/CustomSelect";
import { CreateModalContext } from "layouts/appLayout/layout";
import React, { useContext } from "react";
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd";
import { useTranslation } from "react-i18next";

const InvoiceTable = ({
  articleLines,
  offersResponse,
  isLoadingOffers,
  offersError,
  handleLineChange,
  handleRemoveLine,
  handleAddLine,
  handleDragEnd,
}) => {
  const { t } = useTranslation();
  const { toggleCreateModal } = useContext(CreateModalContext);

  const handleAddOffer = (e) => {
    e.stopPropagation();
    toggleCreateModal(
      <CreateOfferComponent
        onClose={() => {
          toggleCreateModal(<></>);
        }}
      />,
      t("offer.create_form.title")
    );
  };
  return (
    <table className="invoice-table">
      <thead className="table-header">
        <tr>
          {[
            t("invoice.product_service"),
            t("invoice.description"),
            t("invoice.quantity"), // quantity
            t("invoice.price_ht"), // price unit
            t("invoice.reduction"), // discount
            t("invoice.total_ht"), // total before tax,
            t("invoice.tva_percentage"), // VAT
            // "Montant TTC", // total including tax
          ].map((header, idx) => (
            <th key={idx}>{header}</th>
          ))}
        </tr>
      </thead>
      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable droppableId="invoice-lines">
          {(provided) => (
            <tbody ref={provided.innerRef} {...provided.droppableProps}>
              {articleLines.map((line, index) => (
                <Draggable
                  key={index}
                  draggableId={`line-${index}`}
                  index={index}>
                  {(provided) => (
                    <tr
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      {...provided.dragHandleProps}
                      style={{
                        ...provided.draggableProps.style,
                        backgroundColor: "#fff",
                        borderBottom: "1px solid #ddd",
                      }}>
                      {/* Produit/service */}
                      <td>
                        <div className="input-with-button">
                          <CustomSelect
                            style={{ minWidth: 200 }}
                            selectWhat={t("invoice.select_offer")}
                            value={line?.idOffer}
                            onChange={(e) => {
                              const offerId = e.target.value;
                              const selectedOffer = offersResponse?.data?.find(
                                (o) => o._id === offerId
                              );
                              handleLineChange(index, "idOffer", offerId);
                              if (selectedOffer) {
                                handleLineChange(index, {
                                  description: selectedOffer.description || "",
                                  price: selectedOffer.price || 0,
                                });
                              }
                            }}
                            options={offersResponse?.data}
                            isOffer={true}
                            allowCustomValue={false}
                          />
                          <button
                            type="button"
                            className="add-button"
                            onClick={handleAddOffer}
                            title={t("invoice.add_offer")}>
                            +
                          </button>
                        </div>
                      </td>

                      {/* Description */}
                      <td>
                        <textarea
                          value={line.description}
                          onChange={(e) =>
                            handleLineChange(
                              index,
                              "description",
                              e.target.value
                            )
                          }
                          className="table-input border-0"
                          rows={2}
                          style={{ resize: "vertical", width: "100%" }}
                        />
                      </td>

                      {/* Quantité */}
                      <td>
                        <input
                          type="number"
                          value={line.quantity}
                          onChange={(e) =>
                            handleLineChange(index, "quantity", e.target.value)
                          }
                          className="table-input border-0"
                        />
                      </td>

                      {/* Prix unitaire (HT) */}
                      <td>
                        <input
                          type="number"
                          value={line.price}
                          onChange={(e) =>
                            handleLineChange(index, "price", e.target.value)
                          }
                          className="table-input border-0"
                        />
                      </td>

                      {/* Remise */}
                      <td>
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            gap: "4px",
                          }}>
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                            }}>
                            <input
                              type="number"
                              value={line.reductionPercent || ""}
                              onChange={(e) =>
                                handleLineChange(
                                  index,
                                  "reductionPercent",
                                  e.target.value
                                )
                              }
                              placeholder="%"
                              className="table-input border-0"
                              style={{ width: "60px" }}
                            />
                            <span style={{ marginLeft: "4px" }}>%</span>
                          </div>
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                            }}>
                            <input
                              type="number"
                              value={line.reductionAmount || ""}
                              onChange={(e) =>
                                handleLineChange(
                                  index,
                                  "reductionAmount",
                                  e.target.value
                                )
                              }
                              placeholder="Montant"
                              className="table-input border-0"
                              style={{ width: "60px" }}
                            />
                            <span style={{ marginLeft: "4px" }}>€</span>
                          </div>
                        </div>
                      </td>

                      {/* Montant HT */}
                      <td>{line.TotalHt} €</td>

                      {/* TVA */}
                      <td>
                        <CustomSelect
                          isWithMontant={true}
                          style={{ display: "flex", flexDirection: "column" }}
                          line={line}
                          onChange={(e) =>
                            handleLineChange(index, "TVA", e.target.value)
                          }
                          options={[
                            { value: 10, text: "10%" },
                            { value: 20, text: "20%" },
                            { value: 30, text: "30%" },
                          ]}
                          value={
                            [10, 20, 30].includes(Number(line.TVA))
                              ? Number(line.TVA)
                              : "other"
                          }
                          customValue={
                            ![10, 20, 30].includes(Number(line.TVA))
                              ? Number(line.TVA)
                              : ""
                          }
                        />
                      </td>

                      {/* Devise */}
                      {/* <td>
                        <select
                          value={line.devise}
                          onChange={(e) =>
                            handleLineChange(index, "devise", e.target.value)
                          }
                          className="table-input border-0"
                        >
                          <option value="EUR">€</option>
                          <option value="USD">$</option>
                        </select>
                      </td> */}

                      {/* Montant TTC */}
                      {/* <td>
                        {(
                          parseFloat(line.TotalHt || 0) +
                          ((line.TotalHt || 0) * (line.TVA || 0)) / 100
                        ).toFixed(2)}{" "}
                        €
                      </td> */}

                      {/* Optional Remove button */}
                      {articleLines.length > 1 && (
                        <td>
                          <button
                            onClick={() => handleRemoveLine(index)}
                            className="btn-remove">
                            ×
                          </button>
                        </td>
                      )}
                    </tr>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </tbody>
          )}
        </Droppable>
      </DragDropContext>
    </table>
  );
};

export default InvoiceTable;
